// --- persistence/persistence.ts ---
import { logGuideTourDebug } from './utils/tourRuntime'

export const GUIDE_TOUR_STORAGE_PREFIX = 'guide-tour' as const

export type GuideTourStorage = Pick<Storage, 'getItem' | 'setItem'>

export const getGuideTourStorageKey = (
  tourId: string,
  version: number
): string => `${GUIDE_TOUR_STORAGE_PREFIX}:${tourId}:v${version}`

export const isGuideTourSeen = (
  tourId: string,
  version: number,
  storage: GuideTourStorage = localStorage
): boolean =>
  storage.getItem(getGuideTourStorageKey(tourId, version)) === 'seen'

export const markGuideTourSeen = (
  tourId: string,
  version: number,
  storage: GuideTourStorage = localStorage
): void => {
  storage.setItem(getGuideTourStorageKey(tourId, version), 'seen')
}

// --- persistence/progress.ts ---
export type GuideTourProgress = {
  currentStepIndex: number
  viewedStepCount: number
}

export const getGuideTourProgressStorageKey = (
  tourId: string,
  version: number
): string => `${GUIDE_TOUR_STORAGE_PREFIX}:progress:${tourId}:v${version}`

export const getGuideTourProgress = (
  tourId: string,
  version: number,
  storage: GuideTourStorage = localStorage
): GuideTourProgress | null => {
  const raw = storage.getItem(getGuideTourProgressStorageKey(tourId, version))

  if (!raw) {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(raw)

    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'currentStepIndex' in parsed &&
      'viewedStepCount' in parsed &&
      typeof parsed.currentStepIndex === 'number' &&
      typeof parsed.viewedStepCount === 'number'
    ) {
      return {
        currentStepIndex: parsed.currentStepIndex,
        viewedStepCount: parsed.viewedStepCount,
      }
    }
  } catch {
    logGuideTourDebug('Failed to parse guide tour progress from storage', {
      tourId,
      version,
    })
    return null
  }

  return null
}

export const setGuideTourProgress = (
  tourId: string,
  version: number,
  progress: GuideTourProgress,
  storage: GuideTourStorage = localStorage
): void => {
  storage.setItem(
    getGuideTourProgressStorageKey(tourId, version),
    JSON.stringify(progress)
  )
}

export const clearGuideTourProgress = (
  tourId: string,
  version: number,
  storage: GuideTourStorage & Pick<Storage, 'removeItem'> = localStorage
): void => {
  storage.removeItem(getGuideTourProgressStorageKey(tourId, version))
}

export const finishGuideTour = (
  tourId: string,
  version: number,
  storage: GuideTourStorage & Pick<Storage, 'removeItem'> = localStorage
): void => {
  markGuideTourSeen(tourId, version, storage)
  clearGuideTourProgress(tourId, version, storage)
}
